import { ReactSVG } from "react-svg"
import { makeStyles } from "tss-react/mui"
import { AuthoringUtils } from "@adobe/aem-spa-page-model-manager"
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { BOOTSTRAP_GRID_IPAD_PRO_MAX } from "constants-lib/dimensions"
import getContentUrl from "utils-lib/getContentUrl"
import { getWindow } from "utils-lib/getWindow"
import { ButtonPrimary } from "../ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "../ButtonSecondary/ButtonSecondary"
import { Link } from "../Link/LinkContainer"

const useStyles = makeStyles()(
  (theme, { isInEditor, useNarrowContainer, useFlexibleWidth }) => ({
    root: {
      zIndex: `1600 !important`,
      margin: `0 auto`,
      display: `flex`,
      justifyContent: `flex-start`,
      alignItems: `center`,
      overflow: `auto`,
      borderRadius: `.25rem`,
      boxShadow: theme.shadows[5],
      backgroundColor: theme.palette.common.white,
      height: `fit-content`,
      maxWidth: `fit-content !important`,
      [theme.breakpoints.down(`sm`)]: {
        width: `calc(100vw - 1rem)`,
        height: `fit-content`,
        maxHeight: `calc(100vh)`,
      },
    },
    innerContainer: {
      display: `flex`,
      flexDirection: `column`,
      alignItems: `center`,
      backgroundColor: theme.palette.background.white,
      margin: `1rem`,
      padding: `1rem`,
      overflow: `visible`,
      maxWidth: `calc(100% - 31px)`,
      ...(useFlexibleWidth
        ? {
            width: `fit-content !important`,
          }
        : useNarrowContainer
        ? {
            width: `23rem`,
            maxWidth: `23rem`,
            [theme.breakpoints.down(`sm`)]: {
              width: `auto !important`,
            },
          }
        : {
            width: `40.5rem`,
            maxWidth: `40.5rem`,
            [theme.breakpoints.down(`md`)]: {
              padding: `.5rem`,
            },
            [theme.breakpoints.down(`sm`)]: {
              width: `auto !important`,
            },
          }),
      ...(isInEditor ? { borderBottom: `dotted 2px black` } : {}),
    },
    errorMsgLinkTextStyle: {
      color: `rgb(255,0,0,1) !important`,
      textDecoration: `underline`,
    },
    customIconText: {
      margin: `0.7rem 0.5rem 0 0 `,
    },
    titleText: {
      fontSize: `1.5rem`,
      textAlign: `left`,
      color: theme.palette.text.altGray,
      ...theme.typography.fonts.maaxRegular,
      [theme.breakpoints.down(`sm`)]: {
        fontSize: `1.25rem`,
      },
    },
    bodyText: {
      textAlign: `left`,
      color: theme.palette.text.altGray,
    },
    icon: {
      "& > svg": {
        width: `3rem`,
        height: `3rem`,
      },
    },
    close: {
      height: 0,
      padding: 0,
      top: `-.5rem`,
      right: `-1rem`,
      alignSelf: `flex-end`,
      "&:focus": {
        outline: `1px solid ${theme.palette.primary.light} !important`,
        outlineOffset: "0.8rem",
      },
    },
    buttonContainer: {
      display: `flex`,
      justifyContent: `center`,
      flexWrap: `wrap`,
      flexGrow: 1,
      marginBottom: `1rem`,
      width: `100%`,
      [theme.breakpoints.down(`md`)]: {
        minHeight: `8rem`,
        alignItems: `flex-end`,
      },
      [theme.breakpoints.up(`lg`)]: {
        flexWrap: `nowrap`,
      },
    },
    button: {
      marginTop: `1rem`,
      "&.Link": {
        marginTop: 0,
        textDecoration: `underline`,
      },
      ...(useNarrowContainer
        ? {
            width: `100%`,
          }
        : {}),
      [theme.breakpoints.down(`sm`)]: {
        width: `100% !important`,
      },
    },
  }),
)

export const GenericModal = ({
  open = true,
  errorIcon = false,
  onClose = () => {},
  showCloseButton = false,
  closeButtonIcon = `/content/dam/mywm/src-img/icon-close.svg`,
  icon = ``,
  id = undefined,
  title = ``,
  body = ``,
  children = null,
  primaryCTA = ``,
  primaryOnClick = () => {},
  primaryDisabled = false,
  primaryAnalyticsKey = ``,
  secondaryCTA = ``,
  secondaryOnClick = () => {},
  secondaryDisabled = false,
  secondaryAnalyticsKey = ``,
  tertiaryCTA = ``,
  tertiaryOnClick = () => {},
  tertiaryDisabled = false,
  tertiaryAnalyticsKey = ``,
  errorMsg = ``,
  errorMsgLink = ``,
  errorMsgLinkText = ``,
  errorMsgClassName = ``,
  useNarrowContainer = false,
  modalTestingId = ``,
  primaryTestingId = ``,
  secondaryTestingId = ``,
  tertiaryTestingId = ``,
  customClass = ``,
  customCloseClass = ``,
  customDialogueClass = ``,
  hideButtons = false,
  hideErrorMessage = false,
  scroll = undefined,
  useFlexibleWidth = false,
  customButtonContainerClass = {},
  customClassInnerBox = {},
  renderAuthorView = () => {},
  disableEscapeKeyDown = true,
  ariaLabel = `Modal`,
  modalTitle = ``,
  modalDescription = ``,
  useTopRightPosition = false,
}) => {
  const alertIconSvg = `/content/dam/wm/icons/red-alert-exclamation-circle.svg`
  const isInEditor = AuthoringUtils.isInEditor()
  const hasButtons = primaryCTA || secondaryCTA
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const ssrWindow = getWindow()
  const isMobile = ssrWindow.innerWidth <= BOOTSTRAP_GRID_IPAD_PRO_MAX

  const { classes } = useStyles({
    isInEditor,
    useNarrowContainer,
    useFlexibleWidth,
  })

  const renderContent = () => (
    <Box className={`${classes.innerContainer} ${customClass}`}>
      {showCloseButton && (
        <IconButton
          data-testid="GenericModal-Close-Button"
          disableRipple
          aria-label="close"
          onClick={onClose}
          className={`${classes.close} ${customCloseClass}`}
          size="large"
        >
          <ReactSVG wrapper="span" src={getContentUrl(closeButtonIcon)} />
        </IconButton>
      )}
      {icon && (
        <ReactSVG
          wrapper="span"
          className={classes.icon}
          src={getContentUrl(icon)}
        />
      )}
      <Box margin=".5rem 0 0 0" className={customClassInnerBox}>
        <Box className="d-flex">
          {errorIcon && (
            <ReactSVG
              wrapper="span"
              className={classes.customIconText}
              src={getContentUrl(alertIconSvg)}
            />
          )}
          {title && (
            <Typography
              variant="h3"
              component="p"
              gutterBottom
              className={classes.titleText}
            >
              {title}
            </Typography>
          )}
        </Box>
        {body && (
          <Typography
            variant="body1"
            component="p"
            gutterBottom
            className={classes.bodyText}
          >
            {body}
          </Typography>
        )}
        {children}
      </Box>
      {!hideButtons && hasButtons && (
        <Box
          display="flex"
          className={`${classes.buttonContainer} ${customButtonContainerClass}`}
        >
          {secondaryCTA && (
            <ButtonSecondary
              className={`${classes.button} mr-md-3`}
              data-testid={secondaryTestingId}
              onClick={(e) => {
                secondaryOnClick(e)
              }}
              disabled={secondaryDisabled}
              analytics_key={secondaryAnalyticsKey || secondaryCTA}
            >
              {secondaryCTA}
            </ButtonSecondary>
          )}
          {primaryCTA && (
            <ButtonPrimary
              className={classes.button}
              onClick={(e) => {
                onClose()
                primaryOnClick(e)
              }}
              data-testid={primaryTestingId}
              disabled={primaryDisabled}
              analytics_key={primaryAnalyticsKey || primaryCTA}
            >
              {primaryCTA}
            </ButtonPrimary>
          )}
        </Box>
      )}
      <Box className="row" display="flex" width="100%">
        {tertiaryCTA && (
          <Link
            className={classes.button}
            onClick={(e) => {
              tertiaryOnClick(e)
            }}
            data-testid={tertiaryTestingId}
            disabled={tertiaryDisabled}
            analytics_key={tertiaryAnalyticsKey || tertiaryCTA}
          >
            {tertiaryCTA}
          </Link>
        )}
      </Box>
      {!hideErrorMessage && errorMsg && (
        <Typography
          className={`${classes.errorMsg} ${errorMsgClassName} py-2 col-md-12 maax-font-weight-bold`}
          style={{
            color: `rgb(255,0,0,1)`,
          }}
        >
          {errorMsg}{" "}
          {errorMsgLink && (
            <a
              href={errorMsgLink}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.errorMsgLinkTextStyle}
            >
              {errorMsgLinkText}
            </a>
          )}
        </Typography>
      )}
    </Box>
  )

  return isInEditor ? (
    <Box
      data-testid={modalTestingId}
      className={`GenericModal ${classes.root} ${customDialogueClass}`}
    >
      {renderAuthorView() || renderContent()}
    </Box>
  ) : (
    <Dialog
      classes={{
        paper: `GenericModal ${classes.root} ${customDialogueClass}`,
      }}
      fullScreen={fullScreen}
      onClose={onClose}
      open={open}
      // disableBackdropClick // This is deprecated now,  Failed prop type: The prop `disableBackdropClick` of `ForwardRef(Modal)` is deprecated. Use the onClose prop with the `reason` argument to filter the `backdropClick` events.
      disableEscapeKeyDown={disableEscapeKeyDown}
      data-testid={modalTestingId}
      role="dialog"
      PaperProps={{
        "aria-modal": true,
        "aria-label": ariaLabel,
      }}
      sx={
        useTopRightPosition
          ? {
              "& .MuiDialog-container": {
                justifyContent: "flex-end",
                alignItems: "flex-start",
                maxWidth: "1520px",
                margin: "0 auto",
                position: "relative",
              },
              "& .MuiDialog-paper": {
                borderRadius: "10px",
                margin: 0,
                position: "absolute",
                top: isMobile ? "0px" : "56px",
                right: isMobile ? "0px" : "0px",
                width: isMobile ? "100% !important" : "375px",
                maxWidth: isMobile ? "100% !important" : "375px",
                [theme.breakpoints.down(1920)]: {
                  right: "95px",
                },
                [theme.breakpoints.down(`ipadPro`)]: {
                  right: "0px",
                },
              },
            }
          : undefined
      }
      aria-describedby={modalDescription}
      aria-labelledby={modalTitle}
      id={id}
      scroll={scroll}
    >
      {renderContent()}
    </Dialog>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// GenericModal.propTypes = {
//   open: PropTypes.bool,
//   errorIcon: PropTypes.bool,
//   onClose: PropTypes.func,
//   showCloseButton: PropTypes.bool,
//   closeButtonIcon: PropTypes.string,
//   icon: PropTypes.string,
//   id: PropTypes.string,
//   title: PropTypes.string,
//   body: PropTypes.string,
//   children: PropTypes.any,
//   primaryCTA: PropTypes.string,
//   primaryOnClick: PropTypes.func,
//   primaryDisabled: PropTypes.bool,
//   primaryAnalyticsKey: PropTypes.string,
//   secondaryCTA: PropTypes.string,
//   secondaryOnClick: PropTypes.func,
//   secondaryDisabled: PropTypes.bool,
//   secondaryAnalyticsKey: PropTypes.string,
//   tertiaryCTA: PropTypes.string,
//   tertiaryOnClick: PropTypes.func,
//   tertiaryDisabled: PropTypes.bool,
//   tertiaryAnalyticsKey: PropTypes.string,
//   errorMsg: PropTypes.string,
//   errorMsgClassName: PropTypes.string,
//   useNarrowContainer: PropTypes.bool,
//   modalTestingId: PropTypes.string,
//   primaryTestingId: PropTypes.string,
//   secondaryTestingId: PropTypes.string,
//   tertiaryTestingId: PropTypes.string,
//   customClass: PropTypes.string,
//   customDialogueClass: PropTypes.string,
//   hideButtons: PropTypes.bool,
//   hideErrorMessage: PropTypes.bool,
//   scroll: PropTypes.string,
//   useFlexibleWidth: PropTypes.bool,
//   customButtonContainerClass: PropTypes.object,
//   renderAuthorView: PropTypes.func,
//   disableEscapeKeyDown: PropTypes.bool,
//   ariaLabel: PropTypes.string,
//   modalTitle: PropTypes.string,
//   modalDescription: PropTypes.string,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// GenericModal.defaultProps = {
//   open: true,
//   onClose: () => {},
//   showCloseButton: false,
//   closeButtonIcon: `/content/dam/mywm/src-img/icon-close.svg`,
//   icon: ``,
//   id: undefined,
//   title: ``,
//   body: ``,
//   children: null,
//   primaryCTA: ``,
//   primaryOnClick: () => {},
//   primaryDisabled: false,
//   primaryAnalyticsKey: ``,
//   secondaryCTA: ``,
//   secondaryOnClick: () => {},
//   secondaryDisabled: false,
//   secondaryAnalyticsKey: ``,
//   tertiaryCTA: ``,
//   tertiaryOnClick: () => {},
//   tertiaryDisabled: false,
//   tertiaryAnalyticsKey: ``,
//   errorIcon: false,
//   errorMsg: ``,
//   errorMsgClassName: ``,
//   useNarrowContainer: false,
//   modalTestingId: ``,
//   primaryTestingId: ``,
//   scroll: undefined,
//   secondaryTestingId: ``,
//   tertiaryTestingId: ``,
//   customClass: ``,
//   customDialogueClass: ``,
//   hideButtons: false,
//   hideErrorMessage: false,
//   useFlexibleWidth: false,
//   customButtonContainerClass: {},
//   renderAuthorView: () => {},
//   disableEscapeKeyDown: true,
//   ariaLabel: `Modal`,
//   modalTitle: ``,
//   modalDescription: ``,
// }
